import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form } from '@unform/web';
import {
  Container,
  Row,
  Col,
  FormGroup,
  Card,
  CardBody,
  Button,
  Label,
  NavLink,
  CustomInput,
} from 'reactstrap';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { validateCPF, validateCNPJ } from 'validations-br';

import Input from '~/components/Inputs/Input';
import InputMask from '~/components/Inputs/InputMask';
import InputMaskDynamic from '~/components/Inputs/InputMaskDynamic';
import InputCurrency from '~/components/Inputs/InputCurrency';
import InputSelect from '~/components/Inputs/InputSelect';
import Loading from '~/components/Loading';
import PageHeader from '~/components/Common/PageHeader';
import { defineAlignment } from '~/util/newLayoutHelpers';
import { LG } from '~/util/breakpointWidths';
import api from '~/services/api';
import history from '~/services/history';
import { getRole } from '~/util/getRole';
import { useStores } from '~/hooks/useStores';
import IndividualFields from './IndividualFields';
import CompanyFields from './CompanyFields';
import AddressFields from './AddressFields';
import PartnerFields from './PartnerFields';
import SplitRulesSection from './SplitRulesSection';

const SplitReceiver = () => {
  const { receiver_id } = useParams();
  const formRef = useRef();

  const windowSize = useSelector(({ layout }) => layout.windowSize);
  const roles = useSelector(({ user }) => user.profile.roles);

  const role = useMemo(
    () =>
      getRole(roles, [
        'administrator',
        'digital-consultant',
        'cross-manager',
        'salesman',
        'store-manager',
      ]),
    [roles]
  );

  const userId = useSelector(({ user }) => user.profile.id);
  const { stores, loadingStores } = useStores(role, userId);
  const [selectedStore, setSelectedStore] = useState(null);
  const [telephone, setTelephone] = useState('');

  const [recipientData, setRecipientData] = useState(null);
  const [recipientId, setRecipientId] = useState(null);
  const [serviceType, setServiceType] = useState('delivery');
  const [splitType, setSplitType] = useState('percentage');
  const [dynamicRules, setDynamicRules] = useState([]);
  const [transferInterval, setTransferInterval] = useState('Daily');
  const [transferEnabled, setTransferEnabled] = useState(true);
  const [accountType, setAccountType] = useState({
    label: 'Corrente',
    value: 'checking',
  });
  const [amount, setAmount] = useState('');
  const [isLiable, setIsLiable] = useState(false);
  const [transferDay, setTransferDay] = useState('');
  const [transferDayValidationError, setTransferDayValidationError] =
    useState('');

  const [personType, setPersonType] = useState('individual'); // 'individual' ou 'company'
  const [automaticReportEmails, setAutomaticReportEmails] = useState(false);
  const [loading, setLoading] = useState(true);

  const accountTypes = useMemo(
    () => [
      {
        label: 'Poupança',
        value: 'savings',
      },
      {
        label: 'Corrente',
        value: 'checking',
      },
    ],
    []
  );

  const normalizeTransferInterval = interval => {
    const v = String(interval || '')
      .trim()
      .toLowerCase();
    if (v === 'daily') return 'Daily';
    if (v === 'weekly') return 'Weekly';
    if (v === 'monthly') return 'Monthly';
    return 'Daily';
  };

  useEffect(() => {
    const loadReceiverData = async () => {
      try {
        const {
          data: { recipient },
        } = await api.get(`/${role}/recipients/${receiver_id}`);

        setLoading(false);

        setSelectedStore({
          value: recipient.store?.id,
          label: recipient.store?.fantasy_name,
        });

        setRecipientId(recipient.recipient_id);

        setPersonType(recipient.type);

        setServiceType(recipient.service_type);

        setSplitType(recipient.split_rules?.type || 'percentage');
        setAmount(recipient.split_rules?.amount || '');
        setIsLiable(recipient.split_rules?.liable || false);

        setAccountType(
          accountTypes.find(
            type => type.value === recipient.default_bank_account.type
          )
        );

        const enabled = recipient.transfer_settings?.transfer_enabled === true;
        const normalizedInterval = normalizeTransferInterval(
          recipient.transfer_settings?.transfer_interval
        );

        setTransferEnabled(enabled);
        setTransferInterval(normalizedInterval);

        const day = recipient.transfer_settings?.transfer_day;
        if (
          !enabled ||
          normalizedInterval === 'Daily' ||
          !day ||
          Number(day) <= 0
        ) {
          setTransferDay('');
        } else {
          setTransferDay(String(day));
        }

        setTelephone(recipient.telephone);

        setAutomaticReportEmails(
          recipient.automatic_report_emails_enabled === true
        );

        setDynamicRules(recipient.dynamic_split_rules);

        formRef.current?.setData({
          store_id: recipient.store?.id || '',
          recipient_id: recipient.recipient_id || 0,
          name: recipient.name || '',
          email: recipient.email || '',
          telephone: recipient.telephone || '',
          bank: recipient.default_bank_account.bank || '',
          holder_name: recipient.default_bank_account.holder_name || '',
          document: recipient.document || '',
          branch_number: recipient.default_bank_account.branch_number || '',
          account_number: recipient.default_bank_account.account_number
            ? `${recipient.default_bank_account.account_number}-${recipient.default_bank_account.account_check_digit}`
            : '',
          individual_birth_date: recipient.birthdate || '',
          monthly_income: recipient.monthly_income || '',
          professional_occupation: recipient.professional_occupation || '',
          address: recipient.address || {},
          fantasy_name: recipient.fantasy_name || '',
          trading_name: recipient.trading_name || '',
          annual_revenue: recipient.annual_revenue || '',
          partner: recipient.managing_partners?.length
            ? {
                partner_name: recipient.managing_partners[0].name || '',
                partner_email: recipient.managing_partners[0].email || '',
                partner_cpf: recipient.managing_partners[0].document || '',
                partner_birth_date:
                  recipient.managing_partners[0].birthdate || '',
                partner_monthly_income:
                  recipient.managing_partners[0].monthly_income || '',
                partner_professional_occupation:
                  recipient.managing_partners[0].professional_occupation || '',
              }
            : {},
        });
        setRecipientData(recipient);
      } catch (err) {
        console.error(err);
        toast.error('Erro ao carregar os dados do recebedor.');
        setLoading(false);
      }
    };

    loadReceiverData();
  }, [receiver_id, accountTypes, role]);

  const adjustMask = v => {
    const digits = v?.replace(/[^\d]/g, '') || '';
    if (digits.length < 10) return v;
    const cut = digits.length === 10 ? 6 : 7;
    return `(${digits.substring(0, 2)}) ${digits.substring(
      2,
      cut
    )}-${digits.substring(cut)}`;
  };

  const maskBuilder = v => {
    if (!v) return '';
    const mask = adjustMask(v);
    return mask.length >= 6 && mask[5] === '9'
      ? '(99) 99999-9999'
      : '(99) 9999-9999';
  };

  const documentSchema = Yup.string()
    .required('O CPF ou CNPJ é obrigatório')
    .test('cpf-cnpj', 'CPF ou CNPJ inválido', value => {
      if (!value) return false;

      const numericValue = value.replace(/\D/g, '');
      // console.log('Valor enviado para validação:', numericValue);

      if (numericValue.length <= 11) {
        // console.log('Validando CPF');
        return validateCPF(numericValue);
      }
      // console.log('Validando CNPJ');
      return validateCNPJ(numericValue);
    });

  function sanitizeAddress(address) {
    return {
      ...address,
      complement:
        address.complement && address.complement.trim() !== ''
          ? address.complement
          : 'N/A',
    };
  }

  const submitHandler = async formData => {
    const schema = Yup.object().shape({
      name: Yup.string().required('O nome é obrigatório'),
      document: documentSchema,
      email: Yup.string().email('E-mail no formato inválido'),
      bank: Yup.string()
        .length(3, 'Código do Banco deve ter 3 caracteres')
        .required('O Código do Banco é obrigatório'),
      branch_number: Yup.string()
        .matches(/^[0-9]{1,4}(-[0-9]{1,2}){0,1}$/m, 'Formato inválido')
        .required('O Número da Agência é obrigatório'),
      account_number: Yup.string().required('O Número da Conta é obrigatório'),
      account_check_digit: Yup.string().required(
        'O Número Verificador da Conta é obrigatório'
      ),
      transfer_interval: transferEnabled
        ? Yup.string().required('O Intervalo de Transferência é obrigatório')
        : Yup.string().notRequired(),
      transfer_day:
        transferEnabled && transferInterval !== 'Daily'
          ? Yup.string()
              .required('O Dia da Transferência é obrigatório')
              .test(
                'transfer_day_validation',
                transferInterval === 'Weekly'
                  ? 'O Dia da Transferência Semanal deve ser um dia entre 1 e 5'
                  : 'O Dia da Transferência Mensal deve ser um dia entre 1 e 31',
                val => {
                  const n = Number(val);
                  if (transferInterval === 'Weekly') return n >= 1 && n <= 5;
                  if (transferInterval === 'Monthly') return n >= 1 && n <= 31;
                  return true;
                }
              )
          : Yup.string().notRequired(),
      // amount: Yup.string(),
      holder_name: Yup.string().required('O Nome/Razão Social é obrigatório'),
    });

    let transfer_day = 0;

    const accountNumberSplit = formData.account_number?.split('-') || [];
    const accountNumber = accountNumberSplit[0] || null;
    const accountNumberDV = accountNumberSplit[1] || null;

    const branchNumberSplit = formData.branch_number?.split('-') || [];
    const branchNumber = branchNumberSplit[0] || null;
    const branchNumberDV = branchNumberSplit[1] || null;

    try {
      if (transferEnabled && transferInterval !== 'Daily')
        transfer_day = transferDay;

      if (
        transferEnabled &&
        ['Weekly', 'Monthly'].includes(transferInterval) &&
        !transferDay
      ) {
        setTransferDayValidationError('O Dia da Transferência é obrigatório');
      } else {
        setTransferDayValidationError(null);
      }

      await schema.validate(
        {
          ...formData,
          branch_number: branchNumber,
          branch_check_digit: branchNumberDV,
          transfer_interval: transferInterval,
          transfer_day,
          account_number: accountNumber,
          account_check_digit: accountNumberDV,
          // amount,
        },
        {
          abortEarly: false,
        }
      );
    } catch (err) {
      console.error(err);

      setTransferDayValidationError(null);

      const validationErrors = {};

      if (
        transferEnabled &&
        transferInterval === 'Weekly' &&
        (Number(transferDay) < 1 || Number(transferDay) > 5)
      ) {
        setTransferDayValidationError('O Dia da Transferência é obrigatório');
        validationErrors.transfer_day = 'O Dia da Transferência é obrigatório';
      }

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach(e => {
          if (e.path === 'account_check_digit')
            validationErrors.account_number = e.message;
          else validationErrors[e.path] = e.message;
        });
        formRef.current.setErrors(validationErrors);
      }
      return;
    }

    const transferSettingsPayload = { transfer_enabled: transferEnabled };

    if (transferEnabled) {
      transferSettingsPayload.transfer_interval = transferInterval;
      transferSettingsPayload.transfer_day =
        transferInterval === 'Daily' ? 0 : Number(transfer_day || 0);
    }

    const payload = {
      store_id: selectedStore?.value,
      recipient_id: formData.recipient_id,
      description: serviceType === 'delivery' ? 'Entrega' : 'Pagamento',
      name: formData.name,
      email: formData.email,
      automatic_report_emails_enabled: automaticReportEmails,
      telephone: formData.telephone,
      document: formData.document,
      type: personType, // Valores: "individual" (pessoa física) ou "company" (pessoa jurídica).
      service_type: serviceType, // required - Tipo de seviço. Valores: "delivery" ou "payment".
      birthdate: formData.individual_birth_date,
      monthly_income: formData.monthly_income,
      professional_occupation: formData.professional_occupation,
      fantasy_name: formData.fantasy_name,
      trading_name: formData.trading_name,
      annual_revenue: formData.annual_revenue,
      address: sanitizeAddress(formData.address),
      default_bank_account: {
        holder_name: formData.holder_name, // required - Nome da conta. Max: 30.
        holder_type: personType, // required - Tipo do documento. "individual" ou "company".
        holder_document: formData.document, // required - Número do doc. do titular. Deve ser igual ao documento do recebedor. Somente números
        bank: formData.bank, // required - Código do banco. Max 3.
        branch_number: branchNumber, // required - Número da agência. Max 4.
        branch_check_digit: branchNumberDV || null,
        account_number: accountNumber, // required - Número da conta. Max: 13.
        account_check_digit: accountNumberDV, // required - Digitos validadores da conta. Max 2.
        type: accountType.value, // required - Tipo da conta. Valores: "checking" ou "savings".
      },
      transfer_settings: transferSettingsPayload,
      split_rules:
        serviceType === 'payment'
          ? {
              amount: amount ? Number(amount) : null,
              type: splitType, // Tipo de divisão. Os valores possíveis são "flat" ou "percentage".
              liable: isLiable, // Indica se o recebedor vinculado à regra irá receber o restante dos recebíveis após uma divisão.
              charge_processing_fee: isLiable, // Indica se o recebedor é responsável pela transação em caso de chargeback.
              charge_remainder_fee: isLiable, // Indica se o recebedor vinculado à regra será cobrado pelas taxas da transação.
            }
          : null,
      dynamic_split_rules: dynamicRules,
    };

    if (personType === 'company') {
      payload.managing_partners = [
        {
          name: formData.partner?.partner_name,
          email: formData.partner?.partner_email,
          document: formData.partner?.partner_cpf,
          birthdate: formData.partner?.partner_birth_date,
          monthly_income: formData.partner?.partner_monthly_income,
          professional_occupation:
            formData.partner?.partner_professional_occupation,
          telephone: formData.telephone,
        },
      ];
    }

    try {
      await api.put(`/${role}/recipients/${receiver_id}`, payload);
      history.push('/recebedores-split');
    } catch (err) {
      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      } else {
        toast.error(err.message);
      }
    }
  };

  const weekDays = useMemo(
    () => [
      {
        label: 'Segunda-feira',
        value: 1,
      },
      {
        label: 'Terça-feira',
        value: 2,
      },
      {
        label: 'Quarta-feira',
        value: 3,
      },
      {
        label: 'Quinta-feira',
        value: 4,
      },
      {
        label: 'Sexta-feira',
        value: 5,
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <Container className="container new-layout">
        <PageHeader
          pageTitle="Editar Recebedor"
          backTo="/recebedores-split"
          responsivePosition="start"
          desktopPosition="center"
        />
        <Form ref={formRef} onSubmit={submitHandler} initialData={{}}>
          <Card className="card new-layout">
            {loading ? (
              <CardBody className="card-body new-layout">
                <Loading />
              </CardBody>
            ) : (
              <CardBody className="card-body new-layout">
                <Row className="w-100 mx-0">
                  <Col lg={6} className="px-0 pr-lg-2">
                    <FormGroup>
                      <Label>Loja</Label>
                      <InputSelect
                        isDisabled
                        name="store_id"
                        options={stores}
                        onChange={setSelectedStore}
                        value={selectedStore}
                        isSearchable
                        isLoading={loadingStores}
                        loadingMessage={() => 'Buscando lojas...'}
                        placeholder="Selecione a loja"
                        singlePath
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            backgroundColor: state.isDisabled
                              ? '#ced4da9f !important'
                              : '#fff',
                            borderColor: transferDayValidationError
                              ? '#eb5757 !important'
                              : base.borderColor,
                            cursor: state.isDisabled
                              ? 'not-allowed'
                              : base.cursor,
                            opacity: state.isDisabled ? 0.65 : 1,
                            pointerEvents: state.isDisabled ? 'none' : 'auto',
                          }),
                          singleValue: base => ({
                            ...base,
                            color: '#6c757d',
                          }),
                          placeholder: base => ({
                            ...base,
                            color: '#6c757d',
                          }),
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} className="px-0 pl-lg-2">
                    <FormGroup>
                      <Label>ID Recebedor</Label>
                      <Input
                        disabled
                        name="recipient_id"
                        type="text"
                        value={recipientId}
                        placeholder="ID do Recebedor"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup className="mt-3">
                  <Label>Tipo de Serviço</Label>
                  <Row className="w-100 mx-0">
                    <Col lg={4} className="px-lg-0">
                      <NavLink
                        className={`
                      new-layout-nav-pill card d-flex flex-column
                      align-items-center h-fit-content w-100
                      ${serviceType === 'delivery' ? 'checked' : 'unchecked'}
                      font-size-14
                    `}
                        to="#"
                        onClick={() => setServiceType('delivery')}
                      >
                        <span className="text-nowrap">Entrega</span>
                      </NavLink>
                    </Col>
                    <Col lg={4}>
                      <NavLink
                        className={`
                      new-layout-nav-pill card d-flex flex-column
                      align-items-center h-fit-content w-100
                      ${serviceType === 'payment' ? 'checked' : 'unchecked'}
                      font-size-14
                    `}
                        to="#"
                        onClick={() => setServiceType('payment')}
                      >
                        <span className="text-nowrap">Pagamento</span>
                      </NavLink>
                    </Col>
                  </Row>
                </FormGroup>
                <Row className="w-100 mx-0">
                  <Col lg={6} className="px-0 pr-lg-2">
                    <FormGroup className="mb-0">
                      <Row className="mx-0">
                        <Label>Nome</Label>
                        {serviceType === 'payment' && (
                          <CustomInput
                            type="checkbox"
                            id="liable"
                            name="liable"
                            label="Responsável por Chargeback e Taxas"
                            className="ml-auto text-primary z-index-0 mt-1"
                            checked={isLiable}
                            onChange={() => setIsLiable(state => !state)}
                          />
                        )}
                      </Row>
                      <Input
                        disabled
                        name="name"
                        type="text"
                        max="128"
                        placeholder="Nome do Recebedor"
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} className="px-0 pl-lg-2">
                    <FormGroup className="mb-0">
                      <Row className="mx-0">
                        <Label>Email</Label>
                        <CustomInput
                          type="checkbox"
                          id="automatic_report_emails_enabled"
                          name="automatic_report_emails_enabled"
                          label="Receber Relatório de Conciliação"
                          className="ml-auto text-primary z-index-0 mt-1"
                          checked={automaticReportEmails}
                          onChange={() =>
                            setAutomaticReportEmails(state => !state)
                          }
                        />
                      </Row>
                      <Input name="email" type="email" placeholder="Email" />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="w-100 mx-0">
                  <Col lg={6} className="px-0 pr-lg-2">
                    <FormGroup>
                      <Label>Telefone</Label>
                      <InputMask
                        disabled
                        mask={maskBuilder(telephone)}
                        maskChar={null}
                        value={telephone}
                        name="telephone"
                        placeholder="Telefone"
                        type="text"
                        onChange={e => setTelephone(e.target.value)}
                      />
                    </FormGroup>
                  </Col>

                  {serviceType === 'payment' && (
                    <>
                      <Col lg={6} className="px-0 pl-lg-2">
                        <FormGroup>
                          <Label className="d-flex align-items-center justify-content-between">
                            <span>Tipo de Divisão</span>
                            <div>
                              <CustomInput
                                type="radio"
                                id="percentage"
                                name="splitType"
                                label="Percentual"
                                inline
                                checked={splitType === 'percentage'}
                                onChange={() => setSplitType('percentage')}
                              />
                              <CustomInput
                                type="radio"
                                id="flat"
                                name="splitType"
                                label="Valor Fixo"
                                inline
                                checked={splitType === 'flat'}
                                onChange={() => setSplitType('flat')}
                              />
                            </div>
                          </Label>

                          {splitType === 'percentage' ? (
                            <Input
                              name="percentage"
                              type="number"
                              placeholder="Valor (%)"
                              value={amount}
                              onChange={e => {
                                if (Number(e.target.value) <= 100) {
                                  formRef.current.setFieldError(
                                    'percentage',
                                    null
                                  );
                                  setAmount(e.target.value);
                                  return;
                                }
                                formRef.current.setFieldError(
                                  'percentage',
                                  'Valor máximo é 100%'
                                );
                              }}
                              onFocus={() => {
                                formRef.current.setFieldError(
                                  'percentage',
                                  null
                                );
                              }}
                              onBlur={() => {
                                if (amount === '') {
                                  formRef.current.setFieldError(
                                    'percentage',
                                    null
                                  );
                                  return;
                                }
                                if (Number(amount) > 100) {
                                  formRef.current.setFieldError(
                                    'percentage',
                                    'O Percentual deve ser no máximo 100%'
                                  );
                                  return;
                                }
                                formRef.current.setFieldError(
                                  'percentage',
                                  null
                                );
                              }}
                            />
                          ) : (
                            <InputCurrency
                              name="fixed_amount"
                              placeholder="Valor Fixo (R$)"
                              type="text"
                              value={amount}
                              onValueChange={vals => setAmount(vals.floatValue)}
                              currency
                            />
                          )}
                        </FormGroup>
                      </Col>
                    </>
                  )}
                </Row>

                {serviceType === 'payment' && (
                  <SplitRulesSection
                    amount={amount}
                    setAmount={setAmount}
                    splitType={splitType}
                    setSplitType={setSplitType}
                    dynamicRules={dynamicRules}
                    setDynamicRules={setDynamicRules}
                  />
                )}

                <FormGroup className="mt-3">
                  <Label>Tipo de Pessoa</Label>
                  <Row className="w-100 mx-0">
                    <Col lg={4} className="px-lg-0">
                      <NavLink
                        className={`
                          new-layout-nav-pill card d-flex flex-column
                          align-items-center h-fit-content w-100
                          ${
                            personType === 'individual'
                              ? 'checked'
                              : 'unchecked'
                          }
                          font-size-14
                        `}
                        to="#"
                        onClick={() => setPersonType('individual')}
                      >
                        <span className="text-nowrap">Pessoa Física</span>
                      </NavLink>
                    </Col>
                    <Col lg={4}>
                      <NavLink
                        className={`
                          new-layout-nav-pill card d-flex flex-column
                          align-items-center h-fit-content w-100
                          ${personType === 'company' ? 'checked' : 'unchecked'}
                          font-size-14
                        `}
                        to="#"
                        onClick={() => setPersonType('company')}
                      >
                        <span className="text-nowrap">Pessoa Jurídica</span>
                      </NavLink>
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  {personType === 'individual' && <IndividualFields disabled />}
                  {personType === 'company' && (
                    <>
                      <CompanyFields disabled />
                      <PartnerFields disabled />
                    </>
                  )}

                  <AddressFields
                    disabled
                    formRef={formRef}
                    initialAddress={recipientData?.address}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Intervalo de Transferência</Label>
                  <Row className="w-100 mx-0">
                    <Col lg={4} className="px-lg-0">
                      <NavLink
                        className={`
                        new-layout-nav-pill card d-flex flex-column
                        align-items-center h-fit-content w-100
                        ${
                          transferInterval === 'Daily' ? 'checked' : 'unchecked'
                        }
                        font-size-14
                      `}
                        to="#"
                        onClick={() => setTransferInterval('Daily')}
                      >
                        <span className="text-nowrap">Diário</span>
                      </NavLink>
                    </Col>
                    <Col lg={4}>
                      <NavLink
                        className={`
                        new-layout-nav-pill card d-flex flex-column
                        align-items-center h-fit-content w-100
                        ${
                          transferInterval === 'Weekly'
                            ? 'checked'
                            : 'unchecked'
                        }
                        font-size-14
                      `}
                        to="#"
                        onClick={() => setTransferInterval('Weekly')}
                      >
                        <span className="text-nowrap">Semanal</span>
                      </NavLink>
                    </Col>
                    <Col lg={4} className="px-lg-0">
                      <NavLink
                        className={`
                        new-layout-nav-pill card d-flex flex-column
                        align-items-center h-fit-content w-100
                        ${
                          transferInterval === 'Monthly'
                            ? 'checked'
                            : 'unchecked'
                        }
                        font-size-14
                      `}
                        to="#"
                        onClick={() => setTransferInterval('Monthly')}
                      >
                        <span className="text-nowrap">Mensal</span>
                      </NavLink>
                    </Col>
                  </Row>
                </FormGroup>
                <Row className="w-100 mx-0 mt-3">
                  <Col lg={6} className="pl-lg-0">
                    <FormGroup>
                      <Label>Dia da Transferência</Label>
                      {transferInterval === 'Daily' && (
                        <Input
                          name="transfer_day"
                          placeholder="Dia da Transferência"
                          disabled
                        />
                      )}
                      {transferInterval === 'Weekly' && (
                        <>
                          <Select
                            isDisabled
                            name="type"
                            placeholder="Selecione o Dia da Transferência"
                            options={weekDays}
                            value={weekDays.find(
                              day => day.value === transferDay
                            )}
                            onChange={e => setTransferDay(e.value)}
                            onFocus={() => setTransferDayValidationError(null)}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                backgroundColor: state.isDisabled
                                  ? '#ced4da9f !important'
                                  : '#fff',
                                borderColor: transferDayValidationError
                                  ? '#eb5757 !important'
                                  : base.borderColor,
                                cursor: state.isDisabled
                                  ? 'not-allowed'
                                  : base.cursor,
                                opacity: state.isDisabled ? 0.65 : 1,
                                pointerEvents: state.isDisabled
                                  ? 'none'
                                  : 'auto',
                              }),
                              singleValue: base => ({
                                ...base,
                                color: '#6c757d',
                              }),
                              placeholder: base => ({
                                ...base,
                                color: '#6c757d',
                              }),
                            }}
                            theme={theme => ({
                              ...theme,
                              colors: {
                                ...theme.colors,
                                neutral20: transferDayValidationError
                                  ? '#eb5757'
                                  : theme.colors.neutral20,
                              },
                            })}
                            classNamePrefix="select"
                          />
                          {transferDayValidationError && (
                            <span
                              className="text-danger"
                              style={{
                                position: 'unset',
                                whiteSpace: 'unset',
                              }}
                            >
                              {transferDayValidationError}
                            </span>
                          )}
                        </>
                      )}
                      {transferInterval === 'Monthly' && (
                        <Input
                          disabled
                          name="transfer_day"
                          type="number"
                          placeholder="Dia da Transferência"
                          onChange={e => setTransferDay(e.target.value)}
                          value={transferDay}
                        />
                      )}
                    </FormGroup>
                  </Col>
                  <Col lg={6} className="px-0 pl-3">
                    <FormGroup>
                      <Label>Receber Pagamentos Automaticamente</Label>
                      <Row>
                        <Col xs={1}>
                          <CustomInput
                            disabled
                            type="radio"
                            id="mobile-select1"
                            name="transfer_enabled"
                            className="text-primary z-index-0"
                            checked={transferEnabled}
                            onChange={() => setTransferEnabled(true)}
                          />
                        </Col>
                        <Col xs={11}>
                          <label
                            className="new-layout-table-column-label text-dark mb-0"
                            htmlFor="mobile-select1"
                          >
                            Sim
                          </label>
                        </Col>

                        <Col xs={1}>
                          <CustomInput
                            disabled
                            type="radio"
                            id="mobile-select2"
                            name="transfer_enabled"
                            className="text-primary z-index-0"
                            checked={!transferEnabled}
                            onChange={() => setTransferEnabled(false)}
                          />
                        </Col>
                        <Col xs={11}>
                          <label
                            className="new-layout-table-column-label text-dark mb-0"
                            htmlFor="mobile-select2"
                          >
                            Não
                          </label>
                        </Col>
                      </Row>
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="w-100 mx-0 mt-4">
                  <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 px-0 font-size-18 font-weight-bold">
                    Dados Bancários
                  </Col>
                </Row>
                <Row className="w-100 mx-0 mt-3">
                  <Col lg={6} className="pl-lg-0">
                    <FormGroup>
                      <Label>Tipo de Conta</Label>
                      <Select
                        isDisabled
                        name="type"
                        label="Tipo de Conta"
                        placeholder="Selecione o Tipo de Conta"
                        options={[
                          {
                            label: 'Poupança',
                            value: 'savings',
                          },
                          {
                            label: 'Corrente',
                            value: 'checking',
                          },
                        ]}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            backgroundColor: state.isDisabled
                              ? '#ced4da9f !important'
                              : '#fff',
                            cursor: state.isDisabled
                              ? 'not-allowed'
                              : base.cursor,
                            opacity: state.isDisabled ? 0.65 : 1,
                            pointerEvents: state.isDisabled ? 'none' : 'auto',
                          }),
                          singleValue: base => ({
                            ...base,
                            color: '#6c757d',
                          }),
                          placeholder: base => ({
                            ...base,
                            color: '#6c757d',
                          }),
                        }}
                        classNamePrefix="select"
                        value={accountType}
                        onChange={setAccountType}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} className="pr-lg-0">
                    <FormGroup>
                      <Label>Código do Banco</Label>
                      <Input
                        name="bank"
                        type="number"
                        placeholder="Código do Banco"
                        maxLength="3"
                        disabled
                      />
                    </FormGroup>
                  </Col>

                  <Col lg={6} className="pl-lg-0">
                    <FormGroup>
                      <Label>Agência</Label>
                      <InputMask
                        name="branch_number"
                        type="text"
                        placeholder="Agência"
                        maxLength="4"
                        disabled
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} className="pr-lg-0">
                    <FormGroup>
                      <Label>Número da Conta</Label>
                      <Input
                        name="account_number"
                        type="string"
                        placeholder="Número da Conta"
                        disabled
                      />
                    </FormGroup>
                  </Col>

                  <Col lg={6} className="pl-lg-0">
                    <FormGroup>
                      <Label>CPF/CNPJ</Label>
                      <InputMaskDynamic
                        name="document"
                        disabled
                        className="form-control"
                        placeholder="CPF ou CNPJ"
                        onBlur={async e => {
                          try {
                            await documentSchema.validate(e.target.value);
                          } catch (err) {
                            formRef.current.setFieldError(
                              'document',
                              err.message
                            );
                          }
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} className="pr-lg-0">
                    <FormGroup>
                      <Label>Nome/Razão Social</Label>
                      <Input
                        name="holder_name"
                        type="text"
                        placeholder="Nome/Razão Social"
                        disabled
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            )}
          </Card>
          <Row className="w-100 mx-0 my-4">
            <Col
              align={defineAlignment(windowSize, LG, 'center', 'end')}
              className="px-0"
            >
              <Button
                color="primary"
                className="new-layout-btn main px-4 my-4"
                size="lg"
                type="submit"
              >
                Salvar Alterações
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default SplitReceiver;
